import Fuse from "fuse.js";
import type { SettingProductArea } from "./types";

const SEARCH_STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "can",
  "change",
  "configure",
  "edit",
  "for",
  "help",
  "i",
  "in",
  "load",
  "me",
  "my",
  "need",
  "of",
  "off",
  "on",
  "open",
  "please",
  "plz",
  "setting",
  "settings",
  "show",
  "the",
  "to",
  "turn",
  "update",
  "want",
  "with",
]);

export interface WeightedSearchKey {
  name: string;
  weight: number;
}

export interface WeightedSearchResult<TDocument> {
  document: TDocument;
  score: number;
}

export interface WeightedDocumentSearcher<TDocument> {
  search(query: string): WeightedSearchResult<TDocument>[];
}

/**
 * Builds the fuse index once for a document set. Callers with a static
 * document set should hold the searcher to avoid re-indexing per query.
 */
export function createWeightedDocumentSearcher<TDocument>({
  documents,
  keys,
}: {
  documents: readonly TDocument[];
  keys: readonly WeightedSearchKey[];
}): WeightedDocumentSearcher<TDocument> {
  const fuse = new Fuse(documents, {
    keys: [...keys],
    includeScore: true,
    ignoreLocation: true,
    minMatchCharLength: 1,
    shouldSort: true,
    threshold: 0.35,
  });

  return {
    search(query: string): WeightedSearchResult<TDocument>[] {
      const bestResultByDocument = new Map<
        TDocument,
        WeightedSearchResult<TDocument>
      >();
      for (const queryVariant of createSearchQueryVariants(query)) {
        for (const result of fuse.search(queryVariant)) {
          const searchResult = {
            document: result.item,
            score: result.score ?? 0,
          };
          const existing = bestResultByDocument.get(searchResult.document);
          if (!existing || searchResult.score < existing.score) {
            bestResultByDocument.set(searchResult.document, searchResult);
          }
        }
      }

      return [...bestResultByDocument.values()].sort(
        (a, b) => a.score - b.score,
      );
    },
  };
}

export function searchWeightedDocuments<TDocument>({
  documents,
  keys,
  query,
}: {
  documents: readonly TDocument[];
  keys: readonly WeightedSearchKey[];
  query: string;
}): WeightedSearchResult<TDocument>[] {
  return createWeightedDocumentSearcher({ documents, keys }).search(query);
}

export function createSearchQueryVariants(query: string): string[] {
  const variants = [
    query,
    ...query.split(/[-–—:;,.!?]/).map((part) => part.trim()),
  ];

  return uniqueStrings(
    variants.flatMap((variant) => [variant, createKeywordQuery(variant)]),
  ).filter((variant) => variant.length > 0);
}

export function sanitizeSearchLimit(limit: number): number {
  if (!Number.isFinite(limit)) {
    return 0;
  }

  return Math.max(Math.floor(limit), 0);
}

export function toSearchRelevanceScore(fuseScore: number): number {
  const boundedScore = Math.min(Math.max(fuseScore, 0), 1);
  return Number((1 - boundedScore).toFixed(3));
}

export function adjustFuseScoreForProductArea({
  documentProductArea,
  fuseScore,
  primaryProductArea,
  productArea,
}: {
  documentProductArea: SettingProductArea;
  fuseScore: number;
  primaryProductArea: SettingProductArea;
  productArea: SettingProductArea | undefined;
}): number {
  if (!productArea) {
    return fuseScore;
  }

  if (
    primaryProductArea === productArea ||
    documentProductArea === productArea
  ) {
    return Math.max(fuseScore - 0.03, 0);
  }

  return Math.min(fuseScore + 0.05, 1);
}

export function stringifySearchContext(value: unknown): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  return JSON.stringify(value);
}

function createKeywordQuery(query: string): string {
  return query
    .toLowerCase()
    .replace(/[^a-z0-9/_]+/g, " ")
    .split(/\s+/)
    .filter((word) => word && !SEARCH_STOP_WORDS.has(word))
    .join(" ");
}

function uniqueStrings(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    const normalized = value.trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(normalized);
  }

  return result;
}
